import * as React from 'react';
import { Loader2, MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button3D } from '@/components/ui/button-3d';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * QuoteActionBar — a barra flutuante das telas de orçamento (criar e editar).
 *
 * Por que ela existe: a barra tinha CINCO botões em TRÊS linhas e ocupava 142px
 * fixos. Medido no aparelho (Galaxy S25+, viewport de 780px CSS), somada à
 * navegação inferior isso dava **38% da altura permanentemente indisponível** —
 * e o conteúdo por baixo ficava coberto (o cartão do documento, o valor, as
 * etapas da trilha). Numa linha só a barra cai para ~56px.
 *
 * A regra de composição é de INTENÇÃO, não de espaço:
 *  - `primary`   → a ação que o usuário veio fazer (Salvar). Sempre visível.
 *  - `secondary` → a ação de negócio seguinte, quando ela já faz sentido
 *                  (Enviar por WhatsApp só existe depois de haver o que enviar).
 *  - `more`      → tudo o mais, no "⋯".
 *
 * Ação DESABILITADA não fica na barra: dois botões cinzas que não respondem ao
 * toque leem-se como app quebrado. Dentro do menu ela pode aparecer desabilitada,
 * mas aí carrega o MOTIVO junto (`disabledReason`) — item mudo é pior que item
 * ausente.
 */

/**
 * Ícone de "em andamento" para usar como `icon` de uma ação enquanto ela roda.
 * Existe porque a barra renderiza o ícone com uma classe fixa e não teria como
 * acrescentar o `animate-spin` de fora.
 */
export const ActionSpinner = ({ className }: { className?: string }) => (
  <Loader2 className={cn('animate-spin', className)} />
);

export type QuoteAction = {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  disabled?: boolean;
  /**
   * Por que está desabilitada. Renderizado abaixo do rótulo — sem isto o usuário
   * toca, nada acontece e nada explica ("Link e visualização ficam disponíveis
   * depois de salvar" era um texto solto no rodapé da barra antiga).
   */
  disabledReason?: string;
};

type Props = {
  primary?: QuoteAction;
  secondary?: QuoteAction & { color?: 'whatsapp' | 'gray' };
  more?: QuoteAction[];
  /** Marcação do coach-mark do tour de boas-vindas (ver welcomeTour). */
  primaryTourId?: string;
};

export function QuoteActionBar({ primary, secondary, more = [], primaryTourId }: Props) {
  const visibleMore = more.filter(Boolean);

  return (
    // Filha DIRETA do container da página, de propósito: `sticky` só desliza
    // dentro do próprio pai — num wrapper que só contivesse a barra, o pai teria
    // a altura dela e não sobraria percurso.
    <div className="sticky bottom-[calc(var(--bottom-nav-h)+0.5rem)] z-20 mt-6 rounded-2xl border border-border/60 bg-card/95 p-2 shadow-[0_-6px_20px_-10px_rgba(0,0,0,0.35)] backdrop-blur">
      <div className="flex items-center gap-2">
        {primary && (
          <Button3D
            size="sm"
            className="flex-1"
            onClick={primary.onClick}
            disabled={primary.disabled}
            {...(primaryTourId ? { 'data-tour': primaryTourId } : {})}
          >
            <primary.icon className="mr-1.5 h-4 w-4" />
            {primary.label}
          </Button3D>
        )}

        {secondary && (
          <Button3D
            color={secondary.color ?? 'whatsapp'}
            size="sm"
            className="flex-1"
            onClick={secondary.onClick}
            disabled={secondary.disabled}
          >
            <secondary.icon className="mr-1.5 h-4 w-4" />
            {secondary.label}
          </Button3D>
        )}

        {visibleMore.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button3D color="gray" size="sm" className="w-12 shrink-0 px-0" aria-label="Mais ações">
                <MoreHorizontal className="h-5 w-5" />
              </Button3D>
            </DropdownMenuTrigger>
            {/* `side="top"`: a barra vive colada no rodapé, então o menu tem que
                abrir PARA CIMA ou nasce fora da tela (irmão do #21). */}
            <DropdownMenuContent align="end" side="top" sideOffset={8} className="w-60">
              {visibleMore.map((action) => (
                <DropdownMenuItem
                  key={action.id}
                  disabled={action.disabled}
                  onSelect={() => action.onClick()}
                  className="items-start gap-2 py-2.5"
                >
                  <action.icon className="mt-0.5 h-4 w-4 shrink-0" />
                  <span className="flex min-w-0 flex-col">
                    <span className="text-sm">{action.label}</span>
                    {action.disabled && action.disabledReason && (
                      <span className="text-[11px] leading-tight text-muted-foreground">
                        {action.disabledReason}
                      </span>
                    )}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
